<template>
  <section class="smart-recruiters-detail__section smart-recruiters-detail__section-two">
    <loading-indicator
      v-if="!isMounted"
      variant="spinnerLocal"
    />

    <template v-else>
      <div class="smart-recruiters-detail__job-details">
        <headline-fi
          :size="36"
          tag="h3"
          color="brand"
        >
          {{ fields.headlineJobDescription?.value }}
        </headline-fi>
        <!-- eslint-disable vue/no-v-html -->
        <article
          class="smart-recruiters-detail__job-description"
          v-html="jobDescriptionFormatted"
        />
        <!-- eslint-enable vue/no-v-html -->
      </div>

      <div class="smart-recruiters-detail__job-details smart-recruiters-detail__job-qualification">
        <headline-fi
          :size="36"
          tag="h3"
          color="brand"
        >
          {{ fields.headlineQualifications?.value }}
        </headline-fi>
        <!-- eslint-disable vue/no-v-html -->
        <article
          class="smart-recruiters-detail__job-qualification"
          v-html="jobQualificationFormatted"
        />
        <!-- eslint-enable vue/no-v-html -->
      </div>
    </template>
  </section>
</template>

<script setup lang="ts">
import { HeadlineFi, LoadingIndicator } from 'atoms';
import { computed } from 'vue';

import { useFields } from '@/composables/fields';
import { useIsMounted } from '@/composables/isMounted';

defineOptions({ name: 'SmartRecruitersDetailSectionTwo' });

const props = withDefaults(
  defineProps<{
    jobDescription?: string;
    jobQualification?: string;
  }>(),
  {
    jobDescription: '',
    jobQualification: '',
  },
);

const fields = useFields('SmartRecruitersDetailPageFields');

function format(html: string) {
  const element = document.createElement('div');
  element.innerHTML = html;
  const subLists = element.querySelectorAll('* > ul + p + ul');
  if (!subLists.length) {
    element.remove();
    return html;
  }

  const subItemClass = 'smart-recruiters-detail__sub-item';
  subLists.forEach((list) => {
    const headline = list.previousSibling;
    const innerWrapper = document.createElement('div');
    innerWrapper.classList.add(subItemClass);
    list.after(innerWrapper);
    innerWrapper.appendChild(headline as Node);
    innerWrapper.appendChild(list);

    const previousWrapper = document.createElement('div');
    previousWrapper.appendChild(innerWrapper.previousSibling as Node);
    previousWrapper.classList.add(subItemClass);

    const outerWrapper = document.createElement('div');
    outerWrapper.classList.add('smart-recruiters-detail__sub-items');
    innerWrapper.after(outerWrapper);
    outerWrapper.appendChild(previousWrapper);
    outerWrapper.appendChild(innerWrapper);
  });

  const newHtml = String(element.innerHTML);
  element.remove();

  return newHtml;
}

/**
 * `format` needs a DOM, so it must never run while rendering: the server has none and throws, which
 * costs the whole page. The spinner also keeps the server's markup and the client's hydration render
 * identical, so nothing here can mismatch.
 *
 * Read the formatted values only through `formatted` - that guard, not the template's shape, is what
 * keeps `format` off the render path.
 */
const isMounted = useIsMounted();

const formatted = (html: string) => (isMounted.value ? format(html) : html);

const jobDescriptionFormatted = computed(() => formatted(props.jobDescription));
const jobQualificationFormatted = computed(() => formatted(props.jobQualification));
</script>

<style scoped src="./smart-recruiters-detail-section-two" />
